import { appInsights } from "./appInsights";
import { getPendingLoginId } from "./loginTiming";
import { APP_KPI_NAME } from "./utils";

const SUBMIT_STARTED_AT_KEY = "pcs_auth_submit_started_at";
const SUBMIT_METHOD_KEY = "pcs_auth_submit_method";
const MAX_SUBMIT_AGE_MS = 10 * 60 * 1000;
const COOKIE_DOMAIN = ".fujairahport.ae";
const COOKIE_PATH = "/";
const COOKIE_EXPIRY = "Thu, 01 Jan 1970 00:00:00 GMT";
const LOGIN_SUBMIT_TO_SESSION_READY_STAGE = "login_submit_to_session_ready";

const AUTH_METHODS = ["password", "uae_pass", "pof_sso"] as const;

export type AuthMethod = (typeof AUTH_METHODS)[number] | "unknown";

export type LoginSubmitTiming = {
  startedAt: number;
  authMethod: AuthMethod;
};

type TimingSource = "sessionStorage" | "cookie";

let lastConsumedSignature: string | null = null;

function getStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readStorageValue(key: string): string | null {
  const storage = getStorage();
  if (!storage) return null;

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function removeStorageValue(key: string) {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch {
    // Storage failures must never affect authentication.
  }
}

function readCookieValue(name: string): string | null {
  try {
    if (typeof document === "undefined") return null;

    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${name}=`));

    if (!cookie) return null;

    const value = cookie.slice(name.length + 1);
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  } catch {
    return null;
  }
}

function clearCookieValue(name: string, domain?: string) {
  try {
    if (typeof document === "undefined") return;

    const domainAttribute = domain ? `; Domain=${domain}` : "";
    document.cookie = `${name}=; Max-Age=0; Expires=${COOKIE_EXPIRY}; Path=${COOKIE_PATH}${domainAttribute}`;
  } catch {
    // Cookie failures must never affect authentication.
  }
}

function clearSubmitTimingCookies() {
  for (const key of [SUBMIT_STARTED_AT_KEY, SUBMIT_METHOD_KEY]) {
    clearCookieValue(key, COOKIE_DOMAIN);
    clearCookieValue(key);
  }
}

export function clearLoginSubmitTiming() {
  removeStorageValue(SUBMIT_STARTED_AT_KEY);
  removeStorageValue(SUBMIT_METHOD_KEY);
  clearSubmitTimingCookies();
}

function normalizeAuthMethod(method: string | null): AuthMethod {
  return AUTH_METHODS.includes(method as (typeof AUTH_METHODS)[number])
    ? (method as AuthMethod)
    : "unknown";
}

function parseStartedAt(value: string | null, now: number): number | null {
  if (!value || !/^\d+(?:\.\d+)?$/.test(value.trim())) return null;

  const startedAt = Number(value);
  const age = now - startedAt;
  if (!Number.isFinite(startedAt) || startedAt <= 0 || age < 0 || age > MAX_SUBMIT_AGE_MS) {
    return null;
  }

  return startedAt;
}

function readSource(source: TimingSource, now: number): LoginSubmitTiming | null {
  const startedAtValue =
    source === "sessionStorage"
      ? readStorageValue(SUBMIT_STARTED_AT_KEY)
      : readCookieValue(SUBMIT_STARTED_AT_KEY);
  const methodValue =
    source === "sessionStorage"
      ? readStorageValue(SUBMIT_METHOD_KEY)
      : readCookieValue(SUBMIT_METHOD_KEY);
  const startedAt = parseStartedAt(startedAtValue, now);

  if (startedAt !== null) {
    return {
      startedAt,
      authMethod: normalizeAuthMethod(methodValue),
    };
  }

  // A malformed, stale, missing, or incomplete pair is safe to discard.
  if (startedAtValue !== null || methodValue !== null) {
    if (source === "sessionStorage") {
      removeStorageValue(SUBMIT_STARTED_AT_KEY);
      removeStorageValue(SUBMIT_METHOD_KEY);
    } else {
      clearSubmitTimingCookies();
    }
  }

  return null;
}

export function readLoginSubmitTiming(): LoginSubmitTiming | null {
  try {
    const now = Date.now();
    const sessionTiming = readSource("sessionStorage", now);
    if (sessionTiming) return sessionTiming;

    return readSource("cookie", now);
  } catch {
    return null;
  }
}

function trackStageMetric(
  loginId: string,
  durationMs: number,
  authMethod: AuthMethod,
  path?: string,
) {
  try {
    appInsights.trackMetric({
      name: APP_KPI_NAME.PCS_LOGIN_STAGE,
      average: durationMs,
      sampleCount: 1,
      properties: {
        loginId,
        stage: LOGIN_SUBMIT_TO_SESSION_READY_STAGE,
        success: "true",
        durationMs: String(durationMs),
        authMethod,
        ...(path ? { path } : {}),
      } as never,
    });
  } catch {
    // Telemetry must never become part of authentication control flow.
  }

  try {
    appInsights.trackEvent({
      name: "pcs_login_stage_completed",
      properties: {
        loginId,
        stage: LOGIN_SUBMIT_TO_SESSION_READY_STAGE,
        success: true,
        durationMs,
        authMethod,
        ...(path ? { path } : {}),
      },
    });
  } catch {
    // Telemetry must never become part of authentication control flow.
  }
}

export function completeLoginSubmitToSessionReady(context: { path?: string } = {}): boolean {
  console.log("[login-submit-debug]", {
    timing: readLoginSubmitTiming(),
    loginId: getPendingLoginId(),
    cookie: document.cookie,
  });
  try {
    const timing = readLoginSubmitTiming();
    if (!timing) return false;

    const loginId = getPendingLoginId();

    if (!loginId) {
      clearLoginSubmitTiming();
      return false;
    }

    const durationMs = Date.now() - timing.startedAt;
    if (!Number.isFinite(durationMs) || durationMs < 0 || durationMs > MAX_SUBMIT_AGE_MS) {
      clearLoginSubmitTiming();
      return false;
    }

    const signature = `${loginId}:${timing.startedAt}:${timing.authMethod}`;
    if (lastConsumedSignature === signature) {
      clearLoginSubmitTiming();
      return false;
    }

    lastConsumedSignature = signature;
    trackStageMetric(loginId, durationMs, timing.authMethod, context.path);
    clearLoginSubmitTiming();
    return true;
  } catch {
    // Timing and telemetry failures must never affect authentication.
    try {
      clearLoginSubmitTiming();
    } catch {
      // Defensive cleanup is also non-fatal.
    }
    return false;
  }
}
